import { loadModules } from "esri-loader";
import constants from "@/helpers/constants";

const PAGE_SIZE = 10;
const WF_TITLE_PREFIX = "WF";

const SUPPORTED_FIELD_TYPES = new Set([
  "esriFieldTypeBlob",
  "esriFieldTypeDate",
  "esriFieldTypeDouble",
  "esriFieldTypeGeometry",
  "esriFieldTypeGlobalID",
  "esriFieldTypeGUID",
  "esriFieldTypeInteger",
  "esriFieldTypeLong",
  "esriFieldTypeOID",
  "esriFieldTypeRaster",
  "esriFieldTypeSingle",
  "esriFieldTypeSmallInteger",
  "esriFieldTypeString",
  "esriFieldTypeXML",
]);

const TOKEN_EXPIRY_MARGIN_MS = 60000;
const NO_MORE_PAGES = -1;

const fieldWarningCache = new Map();
const tokenCache = new Map();
const pendingTokens = new Map();
const routedUrlPrefixes = new Map();
let interceptorInstalled = false;

function getConfiguredAccounts() {
  return [constants.ArcGISMurcia, constants.ArcGISMurciaSecondary].filter(
    (account) => account?.user && account?.password
  );
}

function resolveAccount(owner) {
  const accounts = getConfiguredAccounts();
  return accounts.find((account) => account.user === owner) || accounts[0];
}

async function mintToken(account) {
  const body = new URLSearchParams({
    username: account.user,
    password: account.password,
    referer: window.location.origin,
    expiration: "120",
    f: "json",
  });
  const response = await fetch(`${constants.ArcGISToken.serverLayer}/generateToken`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  const tokenData = await response.json();
  if (tokenData.error) throw new Error(tokenData.error.message);
  return { token: tokenData.token, expires: tokenData.expires };
}

async function getAccountToken(account) {
  const cached = tokenCache.get(account.user);
  if (cached && cached.expires > Date.now() + TOKEN_EXPIRY_MARGIN_MS) return cached;
  if (!pendingTokens.has(account.user)) {
    pendingTokens.set(
      account.user,
      mintToken(account)
        .then((minted) => {
          tokenCache.set(account.user, minted);
          return minted;
        })
        .finally(() => pendingTokens.delete(account.user))
    );
  }
  return pendingTokens.get(account.user);
}

function mapItem(item, serverUrl, token) {
  const hasThumbnail = typeof item.thumbnail === "string" && item.thumbnail !== "";
  return {
    id: item.id,
    title: item.title,
    thumbnail: hasThumbnail
      ? `${serverUrl}/content/items/${item.id}/info/${item.thumbnail}?token=${token}`
      : null,
    portalItemId: item.id,
    owner: item.owner,
    hasFieldWarning: null,
  };
}

function escapeQueryTerm(term) {
  return term.replace(/"/g, '\\"');
}

function requiresWildfirePrefix(account) {
  return account.user === constants.ArcGISMurcia.user;
}

function buildQuery(account, searchQuery) {
  const titleClause = requiresWildfirePrefix(account) ? ` AND title:${WF_TITLE_PREFIX}*` : "";
  const base = `(type:"Web Map" AND owner:${account.user}${titleClause})`;
  const term = (searchQuery || "").trim();
  return term ? `${base} AND ("${escapeQueryTerm(term)}")` : base;
}

function hasWildfireTitlePrefix(item) {
  return (item.title || "").trim().toUpperCase().startsWith(WF_TITLE_PREFIX);
}

function resolveAccountCursor(start, account) {
  if (typeof start === "object" && start !== null) return start[account.user] ?? 1;
  return start;
}

async function searchAccountWebMaps(account, query, accountStart) {
  const serverUrl = constants.ArcGISToken.serverLayer;
  const { token } = await getAccountToken(account);
  const params = new URLSearchParams({
    q: buildQuery(account, query),
    num: String(PAGE_SIZE),
    start: String(accountStart),
    sortField: "modified",
    sortOrder: "desc",
    f: "json",
    token,
  });
  const response = await fetch(`${serverUrl}/search?${params.toString()}`);
  const data = await response.json();
  if (data.error) throw new Error(data.error.message);
  const items = (data.results || []).map((item) => ({ raw: item, mapped: mapItem(item, serverUrl, token) }));
  return {
    items: requiresWildfirePrefix(account)
      ? items.filter((entry) => hasWildfireTitlePrefix(entry.mapped))
      : items,
    nextStart: data.nextStart ?? NO_MORE_PAGES,
  };
}

async function searchAllAccounts(accounts, query, start) {
  const settled = await Promise.allSettled(
    accounts.map((account) => {
      const accountStart = resolveAccountCursor(start, account);
      if (accountStart === NO_MORE_PAGES) {
        return Promise.resolve({ items: [], nextStart: NO_MORE_PAGES });
      }
      return searchAccountWebMaps(account, query, accountStart);
    })
  );
  const failures = settled.filter((outcome) => outcome.status === "rejected");
  if (failures.length === settled.length) throw failures[0].reason;
  failures.forEach((failure) => console.error(failure.reason));
  return settled.map((outcome) =>
    outcome.status === "fulfilled" ? outcome.value : { items: [], nextStart: NO_MORE_PAGES }
  );
}

async function queryWebMaps({ query = "", start = 1 }) {
  const accounts = getConfiguredAccounts();
  const results = await searchAllAccounts(accounts, query, start);

  const nextStart = {};
  accounts.forEach((account, index) => {
    nextStart[account.user] = results[index].nextStart;
  });
  const items = results
    .flatMap((result) => result.items)
    .sort((a, b) => (b.raw.modified || 0) - (a.raw.modified || 0))
    .map((entry) => entry.mapped);

  return {
    items,
    hasMore: Object.values(nextStart).some((value) => value !== NO_MORE_PAGES),
    nextStart,
  };
}

function layerFieldsHaveUnsupportedType(fields) {
  return (
    Array.isArray(fields) &&
    fields.some((field) => !SUPPORTED_FIELD_TYPES.has(field?.type))
  );
}

async function fetchLayerHasUnsupportedFieldType(layerUrl, token) {
  try {
    const response = await fetch(`${layerUrl}?f=json&token=${token}`);
    const data = await response.json();
    return layerFieldsHaveUnsupportedType(data.fields);
  } catch (error) {
    return false;
  }
}

async function operationalLayersHaveUnsupportedFieldType(layers, token) {
  for (const layer of layers || []) {
    if (Array.isArray(layer?.layers)) {
      if (await operationalLayersHaveUnsupportedFieldType(layer.layers, token)) {
        return true;
      }
      continue;
    }
    if (layer?.url && (await fetchLayerHasUnsupportedFieldType(layer.url, token))) {
      return true;
    }
  }
  return false;
}

async function fetchItemData(portalItemId, token) {
  const serverUrl = constants.ArcGISToken.serverLayer;
  const response = await fetch(`${serverUrl}/content/items/${portalItemId}/data?f=json&token=${token}`);
  return response.json();
}

async function checkUnsupportedFieldTypeLayers(portalItemId, owner) {
  try {
    const { token } = await getAccountToken(resolveAccount(owner));
    const data = await fetchItemData(portalItemId, token);
    return await operationalLayersHaveUnsupportedFieldType(data.operationalLayers, token);
  } catch (error) {
    return false;
  }
}

function hasUnsupportedFieldTypeLayers(portalItemId, owner) {
  if (!fieldWarningCache.has(portalItemId)) {
    fieldWarningCache.set(portalItemId, checkUnsupportedFieldTypeLayers(portalItemId, owner));
  }
  return fieldWarningCache.get(portalItemId);
}

async function ensurePrimaryCredential() {
  const primary = constants.ArcGISMurcia;
  const serverUrl = constants.ArcGISToken.serverLayer;
  const [IdentityManager] = await loadModules(["esri/identity/IdentityManager"]);

  const existing = IdentityManager.findCredential(serverUrl);
  const isValid =
    existing?.userId === primary.user &&
    !(existing.expires && existing.expires < Date.now() + TOKEN_EXPIRY_MARGIN_MS);
  if (isValid) return;

  const { token, expires } = await getAccountToken(primary);
  IdentityManager.registerToken({ server: serverUrl, token, userId: primary.user, expires });
}

function findRoutedAccount(url) {
  for (const [prefix, account] of routedUrlPrefixes) {
    if (url.startsWith(prefix)) return account;
  }
  return undefined;
}

async function routeRequestToken(params) {
  const account = findRoutedAccount(params.url);
  if (!account) return undefined;
  const { token } = await getAccountToken(account);
  params.requestOptions.query = { ...params.requestOptions.query, token };
  return undefined;
}

async function ensureRequestInterceptor() {
  if (interceptorInstalled) return;
  const [esriConfig] = await loadModules(["esri/config"]);
  esriConfig.request.interceptors.push({
    urls: /^https:\/\/[a-z0-9.-]+\.arcgis\.com\//i,
    before: routeRequestToken,
  });
  interceptorInstalled = true;
}

function collectLayerUrls(layers, serverUrl) {
  const urls = [];
  for (const layer of layers || []) {
    if (Array.isArray(layer?.layers)) urls.push(...collectLayerUrls(layer.layers, serverUrl));
    if (layer?.url) urls.push(layer.url, layer.url.replace(/\/\d+$/, ""));
    if (layer?.itemId) urls.push(`${serverUrl}/content/items/${layer.itemId}`);
  }
  return urls;
}

function collectWebMapUrls(data) {
  const serverUrl = constants.ArcGISToken.serverLayer;
  return [
    ...collectLayerUrls(data?.operationalLayers, serverUrl),
    ...collectLayerUrls(data?.baseMap?.baseMapLayers, serverUrl),
  ];
}

async function fetchWebMapUrls(portalItemId, account) {
  try {
    const { token } = await getAccountToken(account);
    return collectWebMapUrls(await fetchItemData(portalItemId, token));
  } catch (error) {
    console.error(error);
    return [];
  }
}

async function prepareWebMapAccess(portalItemId, owner) {
  await ensurePrimaryCredential();
  const account = resolveAccount(owner);
  routedUrlPrefixes.clear();
  if (account.user === constants.ArcGISMurcia.user) return;

  await ensureRequestInterceptor();
  const urls = await fetchWebMapUrls(portalItemId, account);
  const itemUrl = `${constants.ArcGISToken.serverLayer}/content/items/${portalItemId}`;
  [itemUrl, ...urls].forEach((url) => routedUrlPrefixes.set(url, account));
}

export default { queryWebMaps, hasUnsupportedFieldTypeLayers, prepareWebMapAccess };
